import {
  GridApi,
  GridOptions,
  GridReadyEvent,
  NewValueParams,
  ValueFormatterParams,
} from '@ag-grid-community/core'
import { RowNode } from '@ag-grid-community/core/dist/cjs/entities/rowNode'
import { Component, Input, OnDestroy } from '@angular/core'
import { Validators } from '@angular/forms'
import {
  EditableCheckboxRenderer,
  HeliosColumnTypes,
  generateMdiIcon,
  heliosColumnTypes,
  heliosDefaultColWidth,
} from '@helios/ag-grid-config'
import { RootState, selectIsHplAccount } from '@helios/state-management'
import { isNullOrEmpty } from '@helios/utils'
import {
  NotesValidator,
  checkIsNoteValid,
  notesValidationMessages,
} from '@helios/validators'
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap'
import { Store } from '@ngrx/store'
import { NfColumnTypes, NfTooltipParams } from '@northfork/ag-grid'
import {
  AngularColumnTypeEnum,
  InlineEditorParams,
  getInlineEditColumnType,
} from '@northfork/ag-grid-angular'
import { composeFieldPath, formatNumberValue } from '@northfork/basics'
import { take } from 'rxjs/operators'
import { OpenLotRow } from '../../models/open-lot-row.model'
import { SpecLotContext } from '../../models/spec-Lot-context.model'

const field = composeFieldPath([] as OpenLotRow[])

@Component({
  selector: 'hs-spec-lot-modal',
  templateUrl: './spec-lot-modal.component.html',
  styleUrls: ['./spec-lot-modal.component.scss'],
})
export class SpecLotModalComponent implements OnDestroy {
  @Input()
  isLoading = true

  @Input()
  openLotRows: OpenLotRow[] = []

  @Input()
  context: SpecLotContext = {
    lotInventoryMethod: 'Unknown',
    exitUnits: 0.0,
  }
  applyHPL = false
  gridOptions: GridOptions = this.createGridOptions()
  gridApi?: GridApi
  isDataValid = true

  constructor(
    public activeModal: NgbActiveModal,
    private readonly store: Store<RootState>,
  ) {
    this.store
      .select(selectIsHplAccount)
      .pipe(take(1))
      .subscribe(applyHPL => {
        this.applyHPL = applyHPL
        this.gridOptions = this.createGridOptions()
      })
  }

  ngOnDestroy(): void {
    this.gridApi?.destroy()
  }

  onSubmit(): void {
    if (this.gridApi == null || this.isLoading) {
      return
    }

    const data: OpenLotRow[] = []
    this.gridApi.forEachNode(node => data.push(node.data))

    this.activeModal.close(data)
  }

  private createGridOptions(): GridOptions {
    return {
      columnTypes: heliosColumnTypes,
      columnDefs: [
        {
          field: field('validityStatus'),
          headerName: '',
          width: heliosDefaultColWidth.ValidityStatusCol,
          valueGetter: () => '',
          cellRenderer: params => {
            return params.data.validityStatus === 'valid' ||
              !params.data.hasSpecLotMatch
              ? generateMdiIcon('mdi-check-circle', ['text-success'])
              : params.data.validityStatus === 'invalid'
              ? generateMdiIcon('mdi-close-circle-outline', ['text-danger'])
              : generateMdiIcon('mdi-alert-circle-outline', ['text-warning'])
          },
          tooltipField: 'errorMessage',
          tooltipComponentParams: {
            showTooltip: params => !isNullOrEmpty(params?.data.errorMessage),
          } as NfTooltipParams,
        },
        {
          field: field('hasSpecLotMatch'),
          headerName: 'Spec Lot',
          cellRenderer: EditableCheckboxRenderer,
          cellStyle: { 'background-color': 'lightblue' },
          onCellValueChanged: event => this.handleOnCellValueChanged(event),
        },
        {
          field: field('units'),
          type: [
            this.applyHPL ? HeliosColumnTypes.HPLNumber : NfColumnTypes.Number,
            AngularColumnTypeEnum.InlineEdit,
          ],
          cellEditorParams: {
            validators: [Validators.required],
          } as InlineEditorParams,
          cellStyle: { 'background-color': 'lightblue' },
          editable: params => params.data.hasSpecLotMatch,
          onCellValueChanged: event => this.handleOnUnitsValueChanged(event),
        },
        {
          field: field('note'),
          type: getInlineEditColumnType(),
          editable: params => params.data.hasSpecLotMatch,
          cellEditorParams: {
            validators: [NotesValidator()],
            customErrorMessages: notesValidationMessages,
          } as InlineEditorParams,
          cellStyle: { 'background-color': 'lightblue' },
          onCellValueChanged: event => this.handleOnCellValueChanged(event),
        },
        {
          field: field('availableUnits'),
          type: this.applyHPL
            ? HeliosColumnTypes.HPLNumber
            : NfColumnTypes.Number,
        },
        {
          field: field('totalUnitsMatched'),
          headerName: 'Total Units Specified',
          type: this.applyHPL
            ? HeliosColumnTypes.HPLNumber
            : NfColumnTypes.Number,
        },
        {
          field: field('currentAllocation'),
          type: NfColumnTypes.Number,
        },
        {
          field: field('lotEntryDate'),
          headerName: 'Entry Date',
          type: NfColumnTypes.Date,
        },
        {
          field: field('lotEntryPrice'),
          headerName: 'Price',
          type: NfColumnTypes.Number,
          valueFormatter: (params: ValueFormatterParams) =>
            formatNumberValue(params.value, '1.6-6') ?? '',
        },
        {
          field: field('lotOriginalUnits'),
          headerName: 'Original Units',
          type: this.applyHPL
            ? HeliosColumnTypes.HPLNumber
            : NfColumnTypes.Number,
        },
        {
          field: field('lotEntryLegacyId'),
          headerName: 'Legacy Tran Id',
          type: NfColumnTypes.Identifier,
        },
        {
          field: field('lotEntryLegacyOrigId'),
          headerName: 'Legacy Orig Id',
          type: NfColumnTypes.Identifier,
        },
        {
          field: field('lotEntryTranId'),
          headerName: 'Tran UUID',
        },
        {
          field: field('lotEntryTranType'),
          headerName: 'Type',
        },
        {
          field: field('specLotMatchTranIds'),
          headerName: 'Current SLM IDs',
        },
      ],
      defaultColDef: {
        sortable: true,
        filter: true,
      },
      suppressCopyRowsToClipboard: true,
      onGridReady: (event: GridReadyEvent) => this.onGridReady(event),
    }
  }

  private onGridReady(event: GridReadyEvent) {
    this.gridApi = event.api
    this.gridApi.forEachNode(rowNode => this.validateRow(rowNode))
  }

  private validateRow(rowNode: RowNode) {
    const data = rowNode.data
    data.validityStatus = 'valid'
    data.errorMessage = null
    data.units = Number(data.units)

    const errorMessages: string[] = []
    if (data.hasSpecLotMatch) {
      if (data.units == 0) {
        errorMessages.push('Units must not be 0')
      }

      if (Math.sign(data.units) == Math.sign(data.availableUnits)) {
        errorMessages.push('Units must be the opposite sign of Available Units')
      }

      if (Math.sign(data.units) != Math.sign(this.context.exitUnits)) {
        errorMessages.push('Units must be the same sign as Total Exit Units')
      }

      if (Math.abs(data.units) > Math.abs(data.availableUnits)) {
        errorMessages.push(
          `Units (${data.units}) cannot be greater than Available Units (${data.availableUnits})`,
        )
      }

      if (!checkIsNoteValid(data.note)) {
        errorMessages.push('Note must be at least 3 words')
      }

      if (Math.abs(data.units) > Math.abs(this.context.exitUnits)) {
        errorMessages.push(
          `Units (${data.units}) cannot be greater than the Total Exit Units (${this.context.exitUnits})`,
        )
      }

      let totalUnits = 0
      this.gridApi?.forEachNode(node => {
        if (node.data.hasSpecLotMatch) {
          totalUnits += node.data.units
        }
      })
      if (Math.abs(totalUnits) > Math.abs(this.context.exitUnits)) {
        errorMessages.push(
          `Sum of all Units (${totalUnits}) cannot be greater than the Total Exit Units (${this.context.exitUnits})`,
        )
      }
    }

    if (errorMessages.length > 0) {
      data.validityStatus = 'invalid'
      data.errorMessage = errorMessages.join(', ')
    }

    rowNode.setData(data)

    this.checkIfAllDataValid()
  }

  handleOnUnitsValueChanged(event: NewValueParams) {
    if (event.node == null) return

    // Force Units to have the same sign as the Exit Units
    const data = event.node.data
    if (Math.sign(data.units) != Math.sign(this.context.exitUnits)) {
      data.units *= -1
      event.node.setData(data)
    }

    this.handleOnCellValueChanged(event)
  }

  handleOnCellValueChanged(event: NewValueParams) {
    const currentNode = event.node
    if (currentNode == null) return
    event.api.forEachNode(rowNode => this.validateRow(rowNode))
  }

  private checkIfAllDataValid() {
    this.isDataValid = true
    this.gridApi?.forEachNode(node => {
      if (node.data.validityStatus === 'invalid') {
        this.isDataValid = false
        return
      }
    })
  }
}
